import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { Application, Permission, UserPermissionList } from '../models';
import { fromUserPermissionList } from '../reducers';
import { getAllApplicationItems } from './application.selectors';
import { ApplicationApiConstants } from '../application-api-config';
import { authSelectors } from '@content-platform/web-angular-shared-util-auth';

type State = fromUserPermissionList.State;

export const getUserPermissionListRootState: MemoizedSelector<
unknown,
State
> = createFeatureSelector<State>('userPermissionList');
export const getUserPermissionListState: MemoizedSelector<unknown, State> = createSelector(
  getUserPermissionListRootState,
  (state) => state
);

const userPermissionListSelectors = fromUserPermissionList.userPermissionListAdapter.getSelectors(
  getUserPermissionListState
);
export const getAllUserPermissionListItems: (state: unknown) => UserPermissionList[] =
  userPermissionListSelectors.selectAll;
export const getUserPermissionListEntities: (state: unknown) => Dictionary<UserPermissionList> =
  userPermissionListSelectors.selectEntities;

export const getUserPermissionListLoading: MemoizedSelector<unknown, boolean> = createSelector(
  getUserPermissionListState,
  fromUserPermissionList.getUserPermissionListLoading
);

/**
 * Selector to return the permission list for the current user
 */
export const getCurrentUserPermissionList: MemoizedSelector<
unknown,
UserPermissionList | undefined
> = createSelector(
  getUserPermissionListEntities,
  authSelectors.getUserId,
  (userPermissionListEntities, userId) => {
    if (userId) {
      return userPermissionListEntities[userId] as UserPermissionList;
    }
    return undefined;
  }
);

/**
 * Selector to return if the current users permissions list has been loaded
 */
export const getUserPermissionListLoaded: MemoizedSelector<unknown, boolean> = createSelector(
  getCurrentUserPermissionList,
  getUserPermissionListState,
  (permList: UserPermissionList) => !!permList
);

export const getActiveApplicationName: MemoizedSelector<unknown, string> = createSelector(
  getUserPermissionListState,
  () => ApplicationApiConstants.activeAppName
);

export const getActiveApplicationId: MemoizedSelector<
unknown,
string
> = createSelector(getActiveApplicationName, (appName) =>
  (appName || '').replace(' ', '-').toLowerCase()
);

/**
 * Selector to return the applications that the user can access and set the active application
 */
export const getActiveApplication: MemoizedSelector<unknown, any> = createSelector(
  getCurrentUserPermissionList,
  getAllApplicationItems,
  (userPermListEntity, applications: Application[]) => {
    const activeAppName = ApplicationApiConstants.activeAppName;
    let activeApp;
    if (userPermListEntity && userPermListEntity.applications && activeAppName) {
      activeApp = userPermListEntity.applications.find((app) => app.name === activeAppName);
    }
    if (!activeApp && applications && activeAppName) {
      activeApp = applications.find((app) => app.name === activeAppName);
    }
    return activeApp;
  }
);

/**
 * Selector to return the applications that the user can access and set the active application
 */
export const getUserApplications: MemoizedSelector<unknown, any[]> = createSelector(
  getCurrentUserPermissionList,
  getActiveApplication,
  (userPermListEntity, activeApplication: Application) => {
    if (userPermListEntity) {
      return (userPermListEntity.applications || [])
        .filter(
          (app) =>
            !(
              fromUserPermissionList.NON_PRODUCTION_APPS.includes(app.name) &&
              ApplicationApiConstants.production
            )
        )
        .map((app) => ({ ...app, isActive: app === activeApplication }));
    }
    return [];
  }
);

/**
 * Selector to return the applications that the user can access and set the active application
 */
export const getDisplayableUserApplications: MemoizedSelector<unknown, any[]> = createSelector(
  getUserApplications,
  getActiveApplication,
  (userApps, activeApplication: Application) => {
    const activeAppName = activeApplication ? activeApplication.name : '';
    return userApps.filter((app) => app.name !== activeAppName && !app.comingSoon && !app.hidden);
  }
);

/**
 * Selector to return the applications that the user can access and set the active application
 */
export const getUserAdminApplications: MemoizedSelector<
unknown,
Application[] | any[]
> = createSelector(getUserApplications, (userApplications: Application[]) => {
  if (userApplications) {
    return userApplications.filter((app) => {
      if (app) {
        return app.admin;
      }
      return false;
    });
  }
  return [];
});

/**
 * Selector to return the permission that the user can access.
 */
export const getUserPermissions: MemoizedSelector<unknown, Permission[] | any[]> = createSelector(
  getCurrentUserPermissionList,
  (userPermListEntity) => {
    if (userPermListEntity) {
      return userPermListEntity.permissions;
    }
    return [];
  }
);

/**
 * Selector to return is the user has any available permission(s).
 */
export const hasUserPermission: MemoizedSelector<unknown, boolean> = createSelector(
  getCurrentUserPermissionList,
  getUserApplications,
  (userPermListEntity, userApplications) => {
    if (userPermListEntity) {
      return (
        (userPermListEntity.permissions.length > 0 ||
          userApplications.filter((application) => application.admin).length > 0) &&
        userApplications.filter(
          (application) => application.name === ApplicationApiConstants.activeAppName
        ).length > 0
      );
    }
    return false;
  }
);
